import { useCallback, useEffect, useMemo, useState } from 'react';
import { getWizardV2State } from './wizardService';
import WhoStep from './steps/WhoStep';
import WhenStep from './steps/WhenStep';
import NightsStep from './steps/NightsStep';
import LoadingStep from './steps/LoadingStep';
import ExperiencesStep from './steps/ExperiencesStep';
import StyleStep from './steps/StyleStep';
import EmailStep from './steps/EmailStep';
import AdultsStep from './steps/AdultsStep';
import ChildrenStep from './steps/ChildrenStep';
import ChildAgesStep from './steps/ChildAgesStep';
import DepartureStep from './steps/DepartureStep';
import { selectFilterData } from '../mainFilter/selectors';
import SimilarDestinationsStep from './steps/SimilarDestinationsStep';
import { getParamFromUrl, isEntryFlow } from '../../utilities/urlResolver';
import { isMobile } from '../../utilities/helpers';
import WelcomeStep from './steps/WelcomeStep';
import { setStepNames } from './onboardingSlice';
import abTesting from '../../services/abTesting';
import AIAssistant from './ai';
import WizardTypeSelector from './ai/WizardTypeSelector';
import LoadingStepAI from './ai/LoadingStepAI';
import HotelPreferencesStep from './steps/personalizationSteps/HotelPreferencesStep';
import DynamicStep from './steps/personalizationSteps/DynamicStep';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import FacilitiesStep from './steps/personalizationSteps/FacilitiesStep';
import FacilitiesSelectionStep from './steps/personalizationSteps/FacilitiesSelectionStep';
import DynamicStepsSuccess from './steps/personalizationSteps/DynamicStepsSuccess';

const allowedAdultStepTypes = ['friends', 'family'];
const useWizardSteps = () => {
  const dispatch = useAppDispatch();
  const { stepsToShow, similarDestinationsStep } = getWizardV2State();
  const { formV2: form, capacityScreens, dynamicSteps, dynamicStepsStatus, stepsConfig, showAllQuestions } = useAppSelector(state => state.onboarding);
  const [showHotelPreferences, setShowHotelPreferences] = useState(false);
  const entryFlowParams = useAppSelector(state => state.user.entryFlowParams);
  const userContext = localStorage.getItem('hf_user');
  const { who } = form;
  const { searchEngine } = useAppSelector(selectFilterData);
  const wizardType = useAppSelector(state => state.onboarding.type)
  const showSimilarDestStep = !!((isEntryFlow('link-presets') || isEntryFlow('link-presets-welcome')) && getParamFromUrl('pr_similar_dest') && getParamFromUrl('pr_destinations') && similarDestinationsStep);
  const showDeparture = useMemo(() => {
    return (
      stepsToShow.departure &&
      searchEngine?.fromwhere?.length > 1 &&
      !(
        (userContext &&
          userContext?.user?.userLevel === 'USER_EMAIL' &&
          userContext?.user?.user_auth &&
          userContext?.user?.originAirports?.length) ||
        localStorage.getItem('selectedFromwhere')
      ) &&
      !getParamFromUrl('pr_fromwhere')
    );
  }, [searchEngine?.fromwhere?.length, stepsToShow.departure, userContext]);

  const showEmailStep = useMemo(() => {
    function getShowEmailStep() {
      const userContext = JSON.parse(localStorage.getItem('hf_user'))
      if (userContext?.user?.userLevel && userContext?.user?.userLevel !== 'GUEST_ANON') return false;

      return window.holidayFinder.abTest?.emailStep || stepsToShow.emailStep
    }

    return getShowEmailStep();
  }, [stepsToShow.emailStep]);

  if (getParamFromUrl('pr_fromwhere') && getParamFromUrl('pr_fromwhere').length && searchEngine?.fromwhere?.length) {
    // if url has entry flow pr_fromwhere -> save it to the localStorage
    const prevSavedFromwhere = JSON.parse(localStorage.getItem('selectedFromwhere'));
    let prevSavedId = [];
    if (prevSavedFromwhere && prevSavedFromwhere?.fromwhere?.length) {
      prevSavedId = prevSavedFromwhere.fromwhere.map(d => d.id);
    }
    const prFromWhere = getParamFromUrl('pr_fromwhere');
    const selectedFromwhere = searchEngine.fromwhere.map(orig => {
      const newOrigin = { ...orig };
      if (prFromWhere.indexOf(orig.id) > -1 || prevSavedId.indexOf(orig.id) > -1) {
        newOrigin.active = true;
      } else {
        newOrigin.active = false;
      }
      return newOrigin;
    }).filter((item) => item.active);
    if (isEntryFlow('link-presets') || isEntryFlow('link-presets-welcome')) {
      const fromwhereEndDate = new Date();
      fromwhereEndDate.setDate(fromwhereEndDate.getDate() + 365);
      localStorage.setItem(
        'selectedFromwhere',
        JSON.stringify({ fromwhere: selectedFromwhere, date: fromwhereEndDate.getTime() })
      );
    }
  }

  localStorage.setItem('hideCapacityTooltip', 'true');

  const generateDynamicSteps = useCallback(() => {
    let steps = [];

    let filteredDynamicSteps = dynamicSteps;

    if (!showAllQuestions) {
      filteredDynamicSteps = dynamicSteps.filter(step => step.available);
    }
    // show only 6 dynamic steps
    filteredDynamicSteps = filteredDynamicSteps.slice(0, 6);
    const showHotelPreferencesStep = dynamicStepsStatus !== 'rejected' && abTesting.userPrefEnabled
    showHotelPreferencesStep && steps.push({ name: 'hotelPreferences', component: <HotelPreferencesStep showPreferences={setShowHotelPreferences} /> });

    if (filteredDynamicSteps.length > 0 && showHotelPreferences) {
      filteredDynamicSteps.forEach((step, index) => {
        if (step.type === 'USER_PREFERENCE') {
          steps.push({ name: `dynamicStep_${index}`, component: <DynamicStep counterLabel={`${index + 1 }/${filteredDynamicSteps.length}`} data={step} /> });
        }

        if (step.type === 'FACILITY_SELECTION') {
          steps.push({ name: `facilities_${index}`, component: <FacilitiesStep counterLabel={`${index + 1 }/${filteredDynamicSteps.length}`} /> })
          stepsConfig.facilities && steps.push({ name: 'facilities_selection', component: <FacilitiesSelectionStep id={step.id} /> })
        }
      });

      if (filteredDynamicSteps.length > 0) {
        steps.push({ name: 'dynamicStepsSuccess', component: <DynamicStepsSuccess /> })
      }
    }

    return steps;
  }, [dynamicSteps, showHotelPreferences, stepsConfig.facilities, showAllQuestions, dynamicStepsStatus])

  const stepsArray = useMemo(() => {
    const stepsArr = []
    if (isMobile()) {
      stepsArr.push({ name: 'welcome', component: <WelcomeStep /> });
    }

    if ((isMobile() && abTesting.aiAssistant && !abTesting.aiAssistantV2) || (!isMobile() && (abTesting.aiAssistant || abTesting.aiAssistantV2))) {
      const wasAIAssistantShown = localStorage.getItem('wasAIAssistantShown') === 'true';
      if (wasAIAssistantShown || !isMobile()) {
        stepsArr.push({ name: 'wizardTypeSelector', component: <WizardTypeSelector /> });
      }
      if (wizardType === 'assistant') {
        stepsArr.push({ name: 'assistant', component: <AIAssistant /> })
        stepsArr.push({ name: 'loading-ai', component: <LoadingStepAI /> })
      }
    }

    if (isMobile() && abTesting.aiAssistantV2) {
      if (wizardType === 'assistant') {
        stepsArr.push({ name: 'loading-ai', component: <LoadingStepAI /> })
      }
    }

    stepsToShow.who && stepsArr.push({ name: 'who', component: <WhoStep /> });
    if (capacityScreens && allowedAdultStepTypes.includes(who?.id)) {
      stepsArr.push({ name: 'adults', component: <AdultsStep /> });
      who?.id === 'family' && stepsArr.push({ name: 'children', component: <ChildrenStep /> });
      if (who?.id === 'family' && who.capacity?.children > 0) {
        new Array(who.capacity.children).fill(0).forEach((_, index) => {
          stepsArr.push({ name: `childAges_${index}`, component: <ChildAgesStep childIndex={index} /> });
        });
      }
    }
    stepsToShow.when && !(entryFlowParams.startDate && entryFlowParams.endDate) && stepsArr.push({ name: 'when', component: <WhenStep /> });
    stepsToShow.nights && stepsArr.push({ name: 'nights', component: <NightsStep /> });
    if (showSimilarDestStep) {
      stepsArr.push({ name: 'similarDestinations', component: <SimilarDestinationsStep /> });
    }
    stepsToShow.whatTheme && stepsArr.push({ name: 'whatTheme', component: <ExperiencesStep /> });
    stepsToShow.hostingLevelPref && stepsArr.push({ name: 'hostingLevelPref', component: <StyleStep /> });
    showDeparture && stepsArr.push({ name: 'departure', component: <DepartureStep /> });

    stepsArr.push(...generateDynamicSteps());

    showEmailStep && stepsArr.push({ name: 'email', component: <EmailStep /> });
    stepsArr.push({ name: 'loading', component: <LoadingStep /> });
    return stepsArr;
  }, [generateDynamicSteps, capacityScreens, showDeparture, showEmailStep, showSimilarDestStep, stepsToShow.hostingLevelPref, stepsToShow.nights, stepsToShow.whatTheme, stepsToShow.when, stepsToShow.who, who.capacity?.children, who?.id, wizardType, entryFlowParams])

  useEffect(() => {
    dispatch(setStepNames(stepsArray.map(step => step.name)))
  }, [dispatch, stepsArray])

  return stepsArray;
};

export default useWizardSteps;
